import React, { type AllHTMLAttributes, type PropsWithChildren } from 'react';
import cx from 'classnames';
import stylesheet from './Text.css';
export type Appearance =
  | 'heading-2'
  | 'heading-3'
  | 'heading-4'
  | 'heading-5'
  | 'heading-6'
  | 'large'
  | 'large-secondary'
  | 'large-bold'
  | 'medium'
  | 'medium-secondary'
  | 'medium-bold'
  | 'small'
  | 'small-secondary'
  | 'small-bold'
  | 'small-tertiary'
  | 'tiny'
  | 'tiny-secondary'
  | 'tiny-tertiary'
  | 'tiny-bold';

export type Size =
  | 'heading-2'
  | 'heading-3'
  | 'heading-4'
  | 'heading-5'
  | 'heading-6'
  | 'large'
  | 'large-secondary'
  | 'large-bold'
  | 'medium'
  | 'medium-secondary'
  | 'medium-bold'
  | 'small'
  | 'small-secondary'
  | 'small-bold'
  | 'small-tertiary'
  | 'tiny'
  | 'tiny-secondary'
  | 'tiny-tertiary'
  | 'tiny-bold';

export type TextProps = PropsWithChildren<{
  /* The visual appearance of the text. */
  appearance?: Appearance;
  /* Change the visual appearance of the heading. */
  size?: Size;
  as?: 'p' | 'span';
  className?: string;
  textWrap?: 'wrap' | 'nowrap' | 'balance' | 'pretty';
}> &
  AllHTMLAttributes<HTMLElement>;

const convertAppearanceToSize = (appearance?: Appearance): Appearance => {
  const className = appearance || 'medium';
  if (className === 'heading-2') {
    return 'heading-3';
  } else if (className === 'heading-3') {
    return 'heading-5';
  } else if (className === 'heading-4') {
    return 'heading-6';
  }
  return className;
};

const Text = React.forwardRef<
  HTMLParagraphElement & HTMLSpanElement,
  TextProps
>(function Text(
  {
    appearance,
    as: Component = 'p',
    className,
    children,
    textWrap,
    size,
    ...props
  },
  forwardedRef,
) {
  const renderedSize = size || convertAppearanceToSize(appearance);

  return (
    <Component
      className={cx(
        stylesheet.root,
        stylesheet[renderedSize],
        className,
        textWrap && stylesheet[`text-${textWrap}`],
      )}
      ref={forwardedRef}
      {...props}
    >
      {children}
    </Component>
  );
});

export default Text;
